/**
 * ProductSearchModal — browse catalog and pick a product ID (for Tune Metadata Reviewer related products).
 * Reuses AssetSearchModal.css for layout parity with “Search & add asset…”.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from './ToastContainer';
import './AssetSearchModal.css';

export default function ProductSearchModal({ isOpen, onClose, onSelectProduct }) {
  const { showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let resp = await fetch('/api/admin/products', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.status === 401 || resp.status === 403) {
        resp = await fetch('/api/products');
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('[ProductSearchModal]', e);
      showError('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      load();
    }
  }, [isOpen, load]);

  const filtered = useMemo(() => {
    const qRaw = search.trim();
    if (!qRaw) return products;
    const ql = qRaw.toLowerCase();
    const onlyDigits = /^\d+$/.test(qRaw);

    const matches = products.filter((p) => {
      const idStr = String(p.id ?? '');
      const name = (p.name || '').toLowerCase();
      const sku = (p.sku || '').toLowerCase();
      if (onlyDigits) {
        if (idStr === qRaw) return true;
        if (idStr.includes(qRaw)) return true;
        if (Number(p.id) === Number(qRaw)) return true;
      }
      return idStr === ql || idStr.includes(ql) || name.includes(ql) || sku.includes(ql);
    });

    if (!onlyDigits) return matches;

    const rank = (p) => {
      const idStr = String(p.id ?? '');
      if (idStr === qRaw || Number(p.id) === Number(qRaw)) return 0;
      if (idStr.startsWith(qRaw)) return 1;
      return 2;
    };
    return [...matches].sort((a, b) => rank(a) - rank(b));
  }, [products, search]);

  const handlePick = (product) => {
    if (product == null || product.id == null) return;
    onSelectProduct(product);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="asset-search-modal-overlay">
      <div className="asset-search-modal">
        <div className="asset-search-modal-header">
          <h3>Browse products</h3>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="asset-search-modal-content">
          <div className="asset-filters">
            <div className="filter-group" style={{ flex: 1, minWidth: 200 }}>
              <label htmlFor="product-search-modal-q">Search:</label>
              <input
                id="product-search-modal-q"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Product ID, name, or SKU…"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="assets-grid">
            {loading ? (
              <div className="loading">Loading products…</div>
            ) : filtered.length === 0 ? (
              <div className="no-assets">
                {products.length === 0 ? 'No products returned from API.' : 'No matches — try another search.'}
              </div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="asset-item"
                  onClick={() => handlePick(p)}
                  style={{
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    font: 'inherit',
                    fontFamily: 'inherit',
                    padding: 0,
                    margin: 0,
                  }}
                >
                  <div className="asset-preview">
                    {p.image ? (
                      <img src={p.image} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="asset-icon">📦</div>
                    )}
                  </div>
                  <div className="asset-info">
                    <div className="asset-name" title={`${p.id} — ${p.name || ''}`}>
                      <span style={{ fontWeight: 700, marginRight: 8 }}>ID {p.id}</span>
                      <span>{p.name || `(Product ${p.id})`}</span>
                    </div>
                    <div className="asset-meta">
                      <span className="asset-type">Product</span>
                      {p.sku ? <span className="asset-size">{p.sku}</span> : null}
                    </div>
                    {p.archived ? (
                      <div className="asset-description" style={{ color: '#b45309' }}>Archived</div>
                    ) : null}
                  </div>
                </button>
              ))
            )}
          </div>

          {!loading && products.length > 0 && (
            <div style={{ padding: '8px 1.5rem 1rem', fontSize: 12, color: 'var(--color-text-secondary, #64748b)' }}>
              Showing {filtered.length} of {products.length} products · click a row to append its ID to related products
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
